import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import type { ContractingStage } from '@makemydeal/dr-dash-types';

type StageMessageMapping = Readonly<Partial<Record<Exclude<ContractingStage, undefined>, string>>>;
const stageMessageMapping: StageMessageMapping = {
    saveOffer: "Don't click the back button. This process may take up to 2 minutes.",
    get cancel() {
        return this.saveOffer;
    },
    start: "Don't click the back button. Generating new contract. This process may take up to 2 minutes.",
    get waitingForFs() {
        return this.saveOffer;
    }
};

const verifyMessages: string[] = [
    "Don't click the back button. Verifying contract with lender. This process may take up to 2 minutes",
    'Still Processing...',
    'Still processing. This may take up to 2 minutes.',
    'Still Processing...',
    'Verifying contract details with the lender. ',
    'Still Processing...',
    'Still processing. Almost finished.'
];

/** 15 seconds */
const verifyMessageInterval = 15e3;

/**
 * This component assumes that the only contract stages are:
 *     'saveOffer' | 'cancel' | 'start' | 'verify' | undefined
 *
 * As defined by the `ContractingStage` type in `@makemydeal/dr-dash-types`
 * If that changes then this component will need to be updated
 */
export const GenerateContractProgressMessage = () => {
    const stage = useSelector(contractingSelectors.getContractingStage);
    const [verifyMessage, setVerifyMessage] = useState(verifyMessages[0]);

    // This following logic is just cosmetic treatment for cycling once through a series
    // of messages in an animated fashion; it doesn't affect any business behavior
    // istanbul ignore next
    useEffect(() => {
        if (stage === 'verify') {
            let index = 0;
            const interval = setInterval(() => {
                index += 1;
                if (index >= verifyMessages.length) {
                    return clearInterval(interval);
                }

                setVerifyMessage(verifyMessages[index]);
            }, verifyMessageInterval);
            return () => clearInterval(interval);
        }

        return () => null;
    }, [stage]);

    if (!stage) {
        return null;
    }

    const message = stageMessageMapping[stage] || verifyMessage;
    return message;
};
